import React, { useEffect, useRef } from 'react';
import { FiCheck, FiZap } from 'react-icons/fi';
import BillingIntervalSelector from './BillingIntervalSelector';
import { useI18n } from './i18n';
import { BILLING_INTERVALS, getBillingPriceSummary, pricing } from './pricing';
import { trackEvent } from './analytics';

export default function WorkspacePlanCard({ quota, billingInterval, onBillingChange, onUpgrade, pending, signupFirst, hasHistory, historyReady }) {
  const { t } = useI18n();
  const offerRef = useRef(null);
  const seen = useRef(false);
  const price = getBillingPriceSummary(billingInterval);
  const hasQuota = typeof quota?.limit === 'number' && typeof quota?.used === 'number';
  const remaining = hasQuota ? Math.max(0, quota.limit - quota.used) : null;
  const usedAll = hasQuota && remaining === 0;

  useEffect(() => {
    if (!historyReady) return;
    const recordView = () => {
      if (seen.current) return;
      seen.current = true;
      trackEvent('premium_offer_view', {
        cta_location: 'dashboard_premium_card', offer_variant: 'workspace_v1',
        billing_interval: billingInterval, account_type: signupFirst ? 'guest' : 'registered',
        history_state: hasHistory ? 'populated' : 'empty',
      });
    };
    if (!window.IntersectionObserver) { recordView(); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { recordView(); observer.disconnect(); }
    }, { threshold: 0.4 });
    if (offerRef.current) observer.observe(offerRef.current);
    return () => observer.disconnect();
  }, [billingInterval, signupFirst, hasHistory, historyReady]);

  return <aside className="quota-section quota-section--compact conversion-panel workspace-plan" id="workspace-plan" aria-label={t('workspace.yourPlan')}>
    <div className="quota-card free-plan compact-plan-comparison">
      <div className="quota-info">
        <div className="workspace-plan__heading"><h2>{t('overview.freePlan')}</h2><span>{t('workspace.dailyAllowance')}</span></div>
        <p className={usedAll ? 'workspace-plan__exhausted' : ''} aria-live="polite">
          {hasQuota ? t('workspace.freeRemaining', { remaining, limit: quota.limit }) : t('common.loading')}
        </p>
        {hasQuota && <div className="progress-container" role="progressbar" aria-label={t('workspace.dailyAllowance')} aria-valuemin={0} aria-valuemax={quota.limit} aria-valuenow={Math.min(quota.used, quota.limit)}>
          <div className="progress-bar" style={{ width: `${Math.min(100, Math.max(0, quota.used / Math.max(1, quota.limit) * 100))}%` }} />
        </div>}
        <p className="workspace-plan__limits">{t('workspace.freeLimits')}</p>
      </div>
      <div className="upgrade-container compact-upgrade" ref={offerRef}>
        <div className="upgrade-title-row"><span className="workspace-plan__icon"><FiZap aria-hidden="true" /></span><h3>{t('overview.premium')}</h3></div>
        <p className="upgrade-benefits">{t(usedAll ? 'workspace.premiumAtLimit' : hasHistory ? 'workspace.premiumAfterValue' : 'workspace.premiumIntro')}</p>
        <ul className="workspace-plan__benefits">
          {[t('overview.proFeature1'), t('workspace.premiumLimits'), t('overview.proFeature4')].map(benefit => <li key={benefit}><FiCheck aria-hidden="true" />{benefit}</li>)}
        </ul>
        <BillingIntervalSelector value={billingInterval} onChange={onBillingChange} disabled={pending}
          labels={{ ariaLabel: t('billing.intervalLabel'), monthly: t('billing.monthly'), annual: t('billing.annual') }}
          saveLabel={t('billing.savePercent', { percent: pricing.annualDiscountPercent })} className="billing-interval-toggle--compact" />
        <div className="upgrade-price" aria-live="polite">
          <div className="upgrade-price-main"><span className="upgrade-price-amount">{price.monthlyDisplayPrice}</span><span className="upgrade-price-period">{t('billing.perMonth')}</span></div>
          <span className="upgrade-price-sub">{billingInterval === BILLING_INTERVALS.ANNUAL ? t('billing.billedAnnual', { annualTotal: price.annualTotalPrice }) : t('billing.billedMonthly')}</span>
        </div>
        <button type="button" className="upgrade-button" onClick={() => onUpgrade(billingInterval)} disabled={pending} aria-busy={pending}>
          {pending ? t('workspace.openingCheckout') : t(signupFirst ? 'billing.signupPremium' : 'overview.upgradeToPremium')}
        </button>
        {signupFirst && <p className="workspace-plan__signup-note">{t('billing.signupPremiumHint')}</p>}
        <p className="upgrade-trust">{t('overview.moneyBack')}</p>
      </div>
    </div>
  </aside>;
}
